import produce from 'immer';
import { errorMessageFromResponse, errorMessagesListFromResponse } from '../../helpers/errors';
import { createReducer, getErrorStatus, getInitialStatus, getPendingStatus } from '../utils';
import type { OrganizationsStore } from './index';
import type { ReduxActionResponse } from '../../types';
import type { Label } from '../../../models/label/types';
import type { UpdateDefaultReportCustomTextMeta } from './actions';
import { actionTypes } from './actions';
import type { UpdateOrganizationDefaultReportCustomTextResponse } from '../../../typings/api';

const initialState: OrganizationsStore = {
  status: getInitialStatus(),
  organizations: {},
  pendingOrgDomains: {
    tokens: [],
    resendingEmailTokenIds: [],
    status: {
      loading: false,
      error: null,
    },
  },
  orgDomains: {
    domains: [],
    status: {
      loading: false,
      error: null,
    },
  },
  ssoConfigurationStatus: {
    error: null,
    loading: false,
  },
  creatingAuditReportLoading: false,
  labels: {
    status: getInitialStatus(),
    labels: [],
  },
};

const ssoConfigPending = (state: OrganizationsStore): OrganizationsStore =>
  produce(state, draftState => {
    draftState.ssoConfigurationStatus.error = null;
    draftState.ssoConfigurationStatus.loading = true;
  });

const ssoConfigFulfilled = (state: OrganizationsStore): OrganizationsStore =>
  produce(state, draftState => {
    draftState.ssoConfigurationStatus.loading = false;
  });

const ssoConfigRejected = (state: OrganizationsStore, action: { payload: any }): OrganizationsStore =>
  produce(state, draftState => {
    draftState.ssoConfigurationStatus.error = errorMessageFromResponse(action.payload);
    draftState.ssoConfigurationStatus.loading = false;
  });

const fetchOneOrg = {
  // Fetch one action
  [`${actionTypes.FETCH_ONE_ORGANIZATION}_PENDING`]: (state: OrganizationsStore) => produce(state, draftState => {
    draftState.status = getPendingStatus();
  }),
  [`${actionTypes.FETCH_ONE_ORGANIZATION}_FULFILLED`]: (state: OrganizationsStore, action) => produce(state, draftState => {
    draftState.status = getInitialStatus();
    draftState.organizations[action.meta.id] = action.payload.data;
  }),
  [`${actionTypes.FETCH_ONE_ORGANIZATION}_REJECTED`]: (state: OrganizationsStore, action) => produce(state, draftState => {
    draftState.status = getErrorStatus(action.payload);
  }),
};

const updateOrganization = {
  // Update action
  [`${actionTypes.UPDATE_ORGANIZATION}_PENDING`]: (state) => produce(state, draftState => {
    draftState.status = getPendingStatus();
  }),
  [`${actionTypes.UPDATE_ORGANIZATION}_FULFILLED`]: (state, action) => produce(state, draftState => {
    draftState.status = getInitialStatus();
    const current = draftState.organizations[action.payload.data.id];
    draftState.organizations[action.payload.data.id] = { ...current, ...action.payload.data };
  }),
  [`${actionTypes.UPDATE_ORGANIZATION}_REJECTED`]: (state, action) => produce(state, draftState => {
    draftState.status = getErrorStatus(action.payload);
  }),
};

const updateOrganizationDefaultReportCustomText = {
  // Update action
  [`${actionTypes.UPDATE_ORGANIZATION_DEFAULT_REPORT_CUSTOM_TEXT}_PENDING`]: (state: OrganizationsStore) => produce(state, draftState => {
    draftState.status = getPendingStatus();
  }),
  [`${actionTypes.UPDATE_ORGANIZATION_DEFAULT_REPORT_CUSTOM_TEXT}_FULFILLED`]: (
    state: OrganizationsStore,
    action: ReduxActionResponse<UpdateOrganizationDefaultReportCustomTextResponse, UpdateDefaultReportCustomTextMeta>,
  ) => produce(state, draftState => {
    draftState.status = getInitialStatus();
    if (action.meta && action.payload.data) {
      const { reportCustomText } = action.payload.data;
      const { organizationId } = action.meta;
      const current = draftState.organizations[organizationId];
      if (current) {
        draftState.organizations[organizationId] = { ...current, defaultReportCustomText: reportCustomText };
      }
    }
  }),
  [`${actionTypes.UPDATE_ORGANIZATION_DEFAULT_REPORT_CUSTOM_TEXT}_REJECTED`]: (state: OrganizationsStore, action: ReduxActionResponse) => produce(state, draftState => {
    draftState.status = getErrorStatus(action.payload);
  }),
};

const createAuditReport = {
  [`${actionTypes.CREATE_ORG_AUDIT_REPORT}_PENDING`]: (state: OrganizationsStore) => produce(state, draftState => {
    draftState.status.errors = [];
    draftState.creatingAuditReportLoading = true;
  }),
  [`${actionTypes.CREATE_ORG_AUDIT_REPORT}_FULFILLED`]: (state: OrganizationsStore) => produce(state, draftState => {
    draftState.creatingAuditReportLoading = false;
  }),
  [`${actionTypes.CREATE_ORG_AUDIT_REPORT}_REJECTED`]: (state: OrganizationsStore, action) => produce(state, draftState => {
    draftState.status.errors = errorMessagesListFromResponse(action.payload);
    draftState.creatingAuditReportLoading = false;
  }),
};

const fetchPendingOrgDomains = {
  [`${actionTypes.FETCH_PENDING_ORG_DOMAINS}_PENDING`]: (state: OrganizationsStore) => produce(state, draftState => {
    draftState.pendingOrgDomains.status.error = null;
    draftState.pendingOrgDomains.status.loading = true;
  }),
  [`${actionTypes.FETCH_PENDING_ORG_DOMAINS}_FULFILLED`]: (state: OrganizationsStore, action) => produce(state, draftState => {
    draftState.pendingOrgDomains.tokens = action.payload.data;
    draftState.pendingOrgDomains.status.loading = false;
  }),
  [`${actionTypes.FETCH_PENDING_ORG_DOMAINS}_REJECTED`]: (state: OrganizationsStore, action) => produce(state, draftState => {
    draftState.pendingOrgDomains.status.error = errorMessageFromResponse(action.payload);
    draftState.pendingOrgDomains.status.loading = false;
  }),
};

const resendDomainVerificationEmail = {
  [`${actionTypes.RESEND_DOMAIN_VERIFICATION_EMAIL}_PENDING`]: (state: OrganizationsStore, action) => produce(state, draftState => {
    // Add the token ID to the resendingEmailTokenIds array, but don't change the loading property.
    draftState.pendingOrgDomains.resendingEmailTokenIds.push(action.meta.tokenId);
    draftState.pendingOrgDomains.status.error = null;
  }),
  [`${actionTypes.RESEND_DOMAIN_VERIFICATION_EMAIL}_FULFILLED`]: (state: OrganizationsStore, action) => produce(state, draftState => {
    const { pendingOrgDomains } = draftState;
    pendingOrgDomains.resendingEmailTokenIds =
      pendingOrgDomains.resendingEmailTokenIds.filter(id => id !== action.meta.tokenId);
  }),
  [`${actionTypes.RESEND_DOMAIN_VERIFICATION_EMAIL}_REJECTED`]: (state: OrganizationsStore, action) => produce(state, draftState => {
    const { pendingOrgDomains } = draftState;
    pendingOrgDomains.resendingEmailTokenIds =
      pendingOrgDomains.resendingEmailTokenIds.filter(id => id !== action.meta.tokenId);
    draftState.pendingOrgDomains.status.error = errorMessageFromResponse(action.payload);
  }),
};

const fetchOrgDomains = {
  [`${actionTypes.FETCH_ORG_DOMAINS}_PENDING`]: (state: OrganizationsStore) => produce(state, draftState => {
    draftState.orgDomains.status.error = null;
    draftState.orgDomains.status.loading = true;
  }),
  [`${actionTypes.FETCH_ORG_DOMAINS}_FULFILLED`]: (state: OrganizationsStore, action) => produce(state, (draftState: OrganizationsStore) => {
    draftState.orgDomains.status.loading = false;
    draftState.orgDomains.domains = action.payload.data || [];
  }),
  [`${actionTypes.FETCH_ORG_DOMAINS}_REJECTED`]: (state: OrganizationsStore, action) => produce(state, draftState => {
    draftState.orgDomains.status.error = errorMessageFromResponse(action.payload);
    draftState.orgDomains.status.loading = false;
  }),
};

const deleteOrgDomain = {
  [`${actionTypes.DELETE_ORG_DOMAIN}_PENDING`]: (state: OrganizationsStore) => produce(state, draftState => {
    draftState.orgDomains.status.error = null;
    draftState.orgDomains.status.loading = true;
  }),
  [`${actionTypes.DELETE_ORG_DOMAIN}_FULFILLED`]: (state: OrganizationsStore, action) => produce(state, draftState => {
    draftState.orgDomains.status.loading = false;
    draftState.orgDomains.domains = draftState.orgDomains.domains.filter(
      ({ domain }) => domain !== action.meta.domain,
    );
  }),
  [`${actionTypes.DELETE_ORG_DOMAIN}_REJECTED`]: (state: OrganizationsStore, action) => produce(state, draftState => {
    draftState.orgDomains.status.error = errorMessageFromResponse(action.payload);
    draftState.orgDomains.status.loading = false;
  }),
};

const updateSAML = {
  [`${actionTypes.UPDATE_SAML}_PENDING`]: ssoConfigPending,
  [`${actionTypes.UPDATE_SAML}_FULFILLED`]: ssoConfigFulfilled,
  [`${actionTypes.UPDATE_SAML}_REJECTED`]: ssoConfigRejected,
};

const deleteSAML = {
  [`${actionTypes.DELETE_SAML}_PENDING`]: ssoConfigPending,
  [`${actionTypes.DELETE_SAML}_FULFILLED`]: ssoConfigFulfilled,
  [`${actionTypes.DELETE_SAML}_REJECTED`]: ssoConfigRejected,
};

const claimSAMLDomain = {
  [`${actionTypes.CLAIM_SAML_DOMAIN}_PENDING`]: ssoConfigPending,
  [`${actionTypes.CLAIM_SAML_DOMAIN}_FULFILLED`]: ssoConfigFulfilled,
  [`${actionTypes.CLAIM_SAML_DOMAIN}_REJECTED`]: ssoConfigRejected,
};

const claimLDAPDomain = {
  [`${actionTypes.CLAIM_LDAP_DOMAIN}_PENDING`]: ssoConfigPending,
  [`${actionTypes.CLAIM_LDAP_DOMAIN}_FULFILLED`]: ssoConfigFulfilled,
  [`${actionTypes.CLAIM_LDAP_DOMAIN}_REJECTED`]: ssoConfigRejected,
};

const setSSOConfigurationStatus = {
  [actionTypes.SET_SSO_CONFIGURATION_STATUS]: (state: OrganizationsStore, action) => produce(state, draftState => {
    draftState.ssoConfigurationStatus = {
      ...draftState.ssoConfigurationStatus,
      ...action.payload,
    };
  }),
};

const fetchOrgLabels = {
  [`${actionTypes.FETCH_ORG_LABELS}_PENDING`]: (state: OrganizationsStore) => produce(state, draftState => {
    draftState.labels.status = getPendingStatus();
  }),
  [`${actionTypes.FETCH_ORG_LABELS}_FULFILLED`]: (state: OrganizationsStore, action: ReduxActionResponse<{labels: Label[]}>) => produce(state, draftState => {
    draftState.labels.labels = action.payload.data?.labels ?? [];
    draftState.labels.status = getInitialStatus();
  }),
  [`${actionTypes.FETCH_ORG_LABELS}_REJECTED`]: (state: OrganizationsStore, action: ReduxActionResponse<{labels: Label[]}>) => produce(state, draftState => {
    draftState.labels.status = getErrorStatus(action.payload);
  }),
};

const createOrgLabel = {
  [`${actionTypes.CREATE_ORG_LABEL}_PENDING`]: (state: OrganizationsStore) => produce(state, draftState => {
    draftState.labels.status = getPendingStatus();
  }),
  [`${actionTypes.CREATE_ORG_LABEL}_FULFILLED`]: (state: OrganizationsStore, action: ReduxActionResponse<{label: Label}>) => produce(state, draftState => {
    if (action.payload.data?.label) {
      draftState.labels.labels.push(action.payload.data?.label);
    }
    draftState.labels.status = getInitialStatus();
  }),
  [`${actionTypes.CREATE_ORG_LABEL}_REJECTED`]: (state: OrganizationsStore, action: ReduxActionResponse<{labels: Label[]}>) => produce(state, draftState => {
    draftState.labels.status = getErrorStatus(action.payload);
  }),
};

const deleteOrgLabel = {
  [`${actionTypes.DELETE_ORG_LABEL}_PENDING`]: (state: OrganizationsStore) => produce(state, draftState => {
    draftState.labels.status = getPendingStatus();
  }),
  [`${actionTypes.DELETE_ORG_LABEL}_FULFILLED`]: (state: OrganizationsStore, action: ReduxActionResponse<{label: Label}>) => produce(state, draftState => {
    if (action.payload.data?.label) {
      draftState.labels.labels = draftState.labels.labels.filter((label: Label) => label.id !== action.meta.id);
    }
    draftState.labels.status = getInitialStatus();
  }),
  [`${actionTypes.DELETE_ORG_LABEL}_REJECTED`]: (state: OrganizationsStore, action: ReduxActionResponse<{labels: Label[]}>) => produce(state, draftState => {
    draftState.labels.status = getErrorStatus(action.payload);
  }),
};

export default createReducer(initialState, {
  ...fetchOneOrg,
  ...updateOrganization,
  ...updateOrganizationDefaultReportCustomText,
  ...createAuditReport,
  ...fetchPendingOrgDomains,
  ...resendDomainVerificationEmail,
  ...fetchOrgDomains,
  ...deleteOrgDomain,
  ...updateSAML,
  ...deleteSAML,
  ...claimSAMLDomain,
  ...claimLDAPDomain,
  ...setSSOConfigurationStatus,
  ...fetchOrgLabels,
  ...createOrgLabel,
  ...deleteOrgLabel,
});
